/**
 * DropSectionRenderer — section-type dispatch for DropPage.
 *
 * Extracted from DropPage.tsx to keep that file under the agreed 400-line
 * ceiling. What is left here is the SWITCH — the widget each `section.type`
 * resolves to — and nothing else: the icon map, the render context type, the
 * STREAMING case and the two query-bearing cases each moved to `sections/`
 * when this file in turn outgrew the same ceiling. `SECTION_ICONS` is
 * re-exported below so the nav rail's import did not have to move with it.
 */

import React from "react";

import {SECTION_ICONS} from "./sections/sectionIcons";
import {renderStreamingSection} from "./sections/renderStreamingSection";
import {MissionsCase, GameNewsCase} from "./sections/sectionCases";
import {renderEngagementSection} from "./sections/renderEngagementSection";
import type {DropRenderContext} from "./sections/dropRenderContext";
import {GamesSection} from "@/widgets/GamesSection";
import {ChallengesSection} from "@/widgets/ChallengesSection";
import {TournamentsSection} from "@/widgets/TournamentsSection";
import {LeaguesSection} from "@/widgets/LeaguesSection";
import {LaddersSection} from "@/widgets/LaddersSection";
import {GuildsSection} from "@/widgets/GuildsSection";
import {CampaignsSection} from "@/widgets/CampaignsSection";
import {OfficialForSection} from "@/widgets/OfficialForSection";
import {OtherGamesSection} from "@/widgets/OtherGamesSection";
import {InfoSection} from "@/widgets/InfoSection";
import {CustomSection} from "@/widgets/CustomSection";
import {ExclusivesSection} from "@/widgets/ExclusivesSection";

import type {DropSection} from "@/hooks/drop/useGetDrop";


/*
 * `SECTION_ICONS` is re-exported, not declared here — the section-tab nav rail
 * imports it from this module and moving the map should not move its importers.
 */
export {SECTION_ICONS};

/**
 * Render the right widget for a section. Returns `null` for unknown types so
 * the caller can skip rendering without a wrapping conditional everywhere.
 */
export function renderSectionContent(section: DropSection, ctx: DropRenderContext): React.ReactNode {
    const {drop, slug, competitionId} = ctx;

    switch (section.type) {
        case "GAMES":
            /*
             * A release-bearing Drop uses this slot for its sibling titles —
             * each is its own Drop with its own creator team. A title Drop
             * links across to its franchise rather than trying to contain it.
             */
            /*
             * Siblings are a PARENT/CHILD relationship, not release data.
             *
             * This required `drop.release` as well, so a publisher Drop with
             * siblings but no release filled in rendered no franchise links at
             * all — the same mistake as the hero, gating what a page HAS on
             * what phase its title is in. If there are siblings, show them.
             */
            /*
             * A franchise shows its OWN titles; a title shows its siblings.
             *
             * Call of Duty's games are its CHILDREN — `siblingTitles` on that
             * page resolves to other Activision franchises, so the franchise
             * rendered "No Games Available" while owning four titles. Children
             * first, siblings as the fallback, which is the right answer for
             * both: a leaf title has no children and keeps the sibling rail it
             * always had.
             */
            const relatedTitles = drop?.childTitles?.length
                ? drop.childTitles
                : drop?.siblingTitles;

            if (relatedTitles?.length) {
                /*
                 * The API returns release facts (slug, title, status); the
                 * widget wants display copy. Mapped here rather than in the
                 * response so the backend keeps returning facts and the
                 * wording stays a front-end decision.
                 */
                return <OtherGamesSection
                    parentName={drop?.childTitles?.length ? drop?.name : drop?.parentDropName}
                    parentHref={drop?.childTitles?.length
                        ? undefined
                        : drop?.parentDropSlug ? `/drop-in/${drop.parentDropSlug}` : undefined}
                    titles={relatedTitles.map(t => ({
                        name: t.title,
                        imageUrl: t.headerImageUrl,
                        /*
                         * The icon goes in the SQUARE mark — it is not a wide
                         * wordmark, and the card frames the two differently.
                         */
                        iconUrl: t.profileImageUrl,
                        status: t.status === "RELEASED"
                            ? "Out now"
                            : t.status === "BETA" ? "In beta" : "Announced",
                        href: `/drop-in/${t.slug}`,
                    }))}
                />;
            }
            /*
             * Nothing related ⇒ render nothing.
             *
             * This fell through to `GamesSection`, whose empty state is "No
             * Games Available / Check back soon for featured games" — a
             * games-catalog message on a publisher page that simply has no
             * other titles yet. Every other empty section here is silent.
             */
            return competitionId ? <GamesSection competitionId={competitionId} /> : null;
        case "MISSIONS":
            /*
             * Slot-system render: each active questConfig becomes a sponsor
             * engagement in DropEngagementStrip (marquee + tiles). Falls back
             * to nothing when the challenge has no active configs; the legacy
             * MissionsSection is the next fallback for content-heavy missions
             * that haven't been migrated to the slot system yet. Strategy is
             * "new shape wins when it has data; legacy stays available below."
             */
            /*
             * The strip is the canonical render — it returns null when no
             * questConfigs are active, at which point we fall back to the
             * legacy section. This avoids rendering both simultaneously
             * (the previous version stacked them, duplicating content
             * whenever the strip had data).
             */
            return (
                <MissionsCase
                    challengeId={section.challengeId}
                    hubBrandName={drop?.name}
                    hubBrandColor={drop?.themeColors?.primary as string | undefined}
                />
            );
        case "TOURNAMENTS":
            return <TournamentsSection surfaceSlug={slug} />;
        case "LEAGUES":
            return <LeaguesSection surfaceSlug={slug} />;
        case "LADDERS":
            return <LaddersSection
                surfaceSlug={slug}
                competitionId={competitionId}
                heroVideoUrl={drop?.heroSection?.videoUrl}
                heroImageUrl={drop?.heroSection?.heroImageUrl || drop?.creatorHeaderImageUrl}
                hubName={drop?.name}
            />;
        case "LEADERBOARDS":
            return <ChallengesSection
                competitionId={competitionId}
                heroVideoUrl={drop?.heroSection?.videoUrl}
                heroImageUrl={drop?.heroSection?.heroImageUrl || drop?.creatorHeaderImageUrl}
                name={drop?.name}
                hostId={drop?.id}
            />;
        case "GUILDS":
            return <GuildsSection guildId={section.guildId} />;
        case "CHALLENGES":
            return <ChallengesSection
                competitionId={competitionId}
                heroVideoUrl={drop?.heroSection?.videoUrl}
                heroImageUrl={drop?.heroSection?.heroImageUrl || drop?.creatorHeaderImageUrl}
                name={drop?.name}
                hostId={drop?.id}
            />;
        case "CAMPAIGNS":
            /*
             * Drops don't carry retailCampaigns (brand-only concept) — render
             * empty state. Some campaigns are tagged "Exclusives" via id/label;
             * those reroute to the Exclusives widget so the user sees content.
             */
            if (section.label?.toLowerCase().includes("exclusive") || section._id?.toLowerCase().includes("exclusive")) {
                return <ExclusivesSection />;
            }
            return <CampaignsSection campaigns={[]} />;
        case "EXCLUSIVES":
            return <ExclusivesSection />;
        case "STREAMING":
            return renderStreamingSection(ctx);
        case "INFO":
            return <InfoSection hub={drop} />;
        case "CUSTOM":
            return <CustomSection content={section.customContent} />;
        case "NEWS":
            return <GameNewsCase slug={slug} />;
        case "ENGAGEMENT":
            return renderEngagementSection(section, ctx);
        /*
         * Derived from roster membership, so it takes no assignment — unlike
         * ENGAGEMENT, which names a specific thing. It returns null on its own
         * when the creator is on no rosters, and DropPageMain skips sections
         * that render nothing, so an unaffiliated creator gets no empty band.
         */
        case "OFFICIAL_FOR":
            return <OfficialForSection slug={slug} title={section.label} />;
        default:
            return null;
    }
}
